/**
 * User-type catalogue limits, patterns, and protected codes.
 * Per .claude/ui/07-per-screen-flows-iam.md §8 + SOP 15 §"Per-User-Type Landing".
 *
 * `is_system` was DROPPED from the `user_types` table in migration
 * 0021 (per Kiran 2026-05-13). Protection of the seeded rows is now
 * derived from the code being in SYSTEM_USER_TYPE_CODES — the column
 * was redundant when the set is hardcoded and audited via this file.
 */

/**
 * Codes the system literally cannot function without. Their `code`
 * is immutable and they cannot be deleted via the Manage UI;
 * `name` / `description` / `landing_path` / `is_active` remain editable.
 *
 * LOCKED (2026-05-13): the protected set is JUST `admin`. Every
 * other seeded code (`customer`, `sale`, `marketing`,
 * `warehouse_worker`, `warehouse_manager`) is fully editable AND
 * deletable. The FK guard in the DELETE handler still blocks
 * removal of any type that has bound iam_users / referencing
 * landing_page_config / user_type_rules — so deleting a "live"
 * code is rejected with a fast 409.
 *
 * Per SOP 15 Chapter 17 §"DATA MODEL — PROTECTED CODES".
 */
export const SYSTEMUSERTYPECODES = ['admin'] as const
export type SystemUserTypeCode = (typeof SYSTEMUSERTYPECODES)[number]

export function isSystemUserType(code: string): boolean {
  return (SYSTEMUSERTYPECODES as readonly string[]).includes(code)
}

/**
 * The user_types.code every signing-up customer is bound to (Rule #23).
 * Locked 2026-05-14 per Kiran: "the customer who ever registers will
 * automatically be assigned to customer user type." Auth worker uses
 * this to look up the post-registration / post-login landing_path
 * from user_types instead of hardcoding `/account` in the handler.
 */
export const CUSTOMERUSERTYPECODE = 'customer'

/** Safe fallback if the customer user_types row is somehow missing /
 *  inactive. Keeps a fresh deploy from locking signup out. The customer
 *  landing is the storefront (migration 0067). */
export const CUSTOMERLANDINGFALLBACK = '/shop'

/** Code grammar — kebab-friendly lowercase, immutable after create. */
export const USERTYPECODEPATTERN = /^[a-z0-9_]{2,32}$/

export const USERTYPENAMEMAX = 64
export const USERTYPEDESCMAX = 256

/** Landing path grammar — must start with `/`, no protocol. */
export const LANDINGPATHPATTERN = /^\/[a-z0-9\-_/:]+$/i

/**
 * Paths that must NEVER be used as a landing destination because
 * they are the login flow itself — would create a redirect loop.
 */
export const LANDINGPATHBLOCKLIST: readonly string[] = [
  '/login',
  '/account/login'
] as const

export function isBlockedLandingPath(path: string): boolean {
  if (path.includes('://')) return true
  return LANDINGPATHBLOCKLIST.includes(path)
}
